import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@shadcn/components/ui/card';
import { Button } from '@shadcn/components/ui/button';
import { Switch } from '@shadcn/components/ui/switch';
import { Badge } from '@shadcn/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@shadcn/components/ui/dropdown-menu';
import { MoreHorizontal, Edit, Copy, Trash2, Play, Clock } from 'lucide-react';
import { TRIGGER_TYPE_LABELS } from '../../constants';
import moment from 'moment';
import cronstrue from 'cronstrue';

const describeCron = (cron) => {
  try { return cronstrue.toString(cron, { use24HourTimeFormat: true }); }
  catch { return cron; }
};

const getCronExpression = (rule) => {
  let cron = rule.trigger?.config?.cronExpression || rule.trigger?.config?.schedule;
  if (!cron) {
    const triggerNode = rule.canvas?.nodes?.find(n => n.type === 'trigger');
    cron = triggerNode?.data?.schedule;
  }
  if (!cron) return null;
  return cron.replace(/['"]/g, '').trim() || null;
};

const RuleCard = ({ rule, onToggle, onDelete, onExecute, onDuplicate, showScheduleInfo }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const cronExpression = useMemo(
    () => (showScheduleInfo ? getCronExpression(rule) : null),
    [rule.trigger?.config?.cronExpression, rule.trigger?.config?.schedule, rule.canvas, showScheduleInfo],
  );

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="pt-0.5 shrink-0">
              <Switch
                checked={rule.enabled}
                onCheckedChange={() => onToggle(rule._id)}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Link
                  to={`/automation-engine/rules/${rule._id}/edit`}
                  className="text-base font-medium hover:underline truncate"
                >
                  {rule.name}
                </Link>
                <Badge variant="secondary" className="text-xs shrink-0">
                  {TRIGGER_TYPE_LABELS[rule.trigger?.type] || rule.trigger?.type}
                </Badge>
              </div>
              {rule.description && (
                <p className="text-sm text-gray-500 truncate">{rule.description}</p>
              )}
              {showScheduleInfo && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs">
                  {cronExpression ? (
                    <span className="text-gray-500">
                      {describeCron(cronExpression)}
                    </span>
                  ) : (
                    <span className="text-gray-400 italic">No schedule configured</span>
                  )}
                  <span className="flex items-center gap-1 text-gray-400">
                    <Clock size={12} />
                    {rule.statistics?.lastExecutedAt
                      ? `Last run ${moment(rule.statistics.lastExecutedAt).fromNow()}`
                      : 'Not yet run'}
                  </span>
                </div>
              )}
              {!showScheduleInfo && (
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                  {rule.statistics?.executionCount > 0 && (
                    <span>{rule.statistics.executionCount} runs</span>
                  )}
                  {rule.statistics?.lastExecutedAt && (
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      Last run {moment(rule.statistics.lastExecutedAt).fromNow()}
                    </span>
                  )}
                </div>
              )}
              {rule.tags?.length > 0 && (
                <div className="flex gap-1 mt-2">
                  {rule.tags.map(tag => (
                    <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 ml-4 shrink-0">
            {(rule.trigger?.type === 'manual' || (rule.trigger?.type === 'schedule' && showScheduleInfo)) && rule.enabled && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onExecute(rule._id)}
                title="Run now"
              >
                <Play size={14} />
              </Button>
            )}
            <DropdownMenu onOpenChange={(open) => { if (!open) setConfirmDelete(false); }}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreHorizontal size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <Link to={`/automation-engine/rules/${rule._id}/edit`}>
                  <DropdownMenuItem>
                    <Edit size={14} className="mr-2" />
                    Edit
                  </DropdownMenuItem>
                </Link>
                <DropdownMenuItem onClick={() => onDuplicate?.(rule._id)}>
                  <Copy size={14} className="mr-2" />
                  Create Copy
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {confirmDelete ? (
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-600"
                    onClick={() => {
                      onDelete(rule._id);
                      setConfirmDelete(false);
                    }}
                  >
                    <Trash2 size={14} className="mr-2" />
                    Confirm Delete
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-600"
                    onClick={(e) => {
                      e.preventDefault();
                      setConfirmDelete(true);
                    }}
                  >
                    <Trash2 size={14} className="mr-2" />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default React.memo(RuleCard);
